import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateChatDepartmentRequest,
  UpdateChatDepartmentRequest,
} from '@nucleus-commerce/api-client/admin';

import { chatSettingsApi } from '../api/chat-settings-api';

import { chatSettingsKeys } from './chat-settings-keys';

/** Making one department the default clears the flag on the others, so every change refetches. */
export function useDepartmentMutations(storeId: string) {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: chatSettingsKeys.departments(storeId) });

  const create = useMutation({
    mutationFn: (body: CreateChatDepartmentRequest) =>
      chatSettingsApi.createDepartment(storeId, body),
    meta: { successMessage: 'chatSettings:toasts.departmentSaved', silentError: true },
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateChatDepartmentRequest }) =>
      chatSettingsApi.updateDepartment(storeId, id, body),
    meta: { successMessage: 'chatSettings:toasts.departmentSaved', silentError: true },
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => chatSettingsApi.deleteDepartment(storeId, id),
    meta: { successMessage: 'chatSettings:toasts.departmentDeleted' },
    onSuccess: refresh,
  });
  return { create, update, remove };
}
